import { HttpService, IHttpClientModel } from "@ir/core/http";
import { AxiosError } from "axios";
import { AppConfig } from "../config";
import { createLogger, ILogger } from "@/infrastructure/services/logger";

const _appConfig = AppConfig.getInstance();

const API_BASE_URL = _appConfig.API_BASE_URL;
const TIMEOUT = _appConfig.TIMEOUT;
const TIMESTAMP = _appConfig.TIMESTAMP;
const TIMEZONE = _appConfig.TIMEZONE;

/**
 * @example
 * const NetworkManager = new NetworkManager();
 * NetworkManager.get<ResponseType>("/path/to/api", { query: "params" });
 * NetworkManager.post<ResponseType>("/path/to/api", { data: "params" });
 * NetworkManager.put<ResponseType>("/path/to/api", { data: "params" });
 * NetworkManager.delete<ResponseType>("/path/to/api", { query: "params" });
 */

export class NetworkManager {
  public readonly httpService: HttpService;
  private readonly networkConfig: IHttpClientModel;
  private readonly logger: ILogger = createLogger("NetworkManager");

  constructor(_networkConfig?: Omit<IHttpClientModel, "baseURL">) {
    this.networkConfig = {
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: TIMEOUT,
      ..._networkConfig,
    };
    this.httpService = new HttpService(this.networkConfig);
    this.handleRequest();
    this.handleResponse();
  }

  protected handleRequest() {
    const _httpService = this.httpService;
    if (_httpService.interceptors.request) {
      _httpService.interceptors.request.use(config => {
        const defaultData = {
          timestamp: TIMESTAMP,
          timezone: TIMEZONE,
        };
        if (config.method === "get") {
          config.transformRequest = data => {
            return {
              ...data,
              ...defaultData,
            };
          };
        } else if (config.data instanceof FormData) {
            config.data.append("timestamp", TIMESTAMP.toString());
            config.data.append("timezone", TIMEZONE);
          } else {
            config.data = {
              ...config.data,
              ...defaultData,
            };
          }
        return config;
      });
    }
  }

  protected handleResponse() {
    this.httpService.interceptors.response.use(
      response => {
        return response.data;
      },
      (error: AxiosError<{ message?: string; status?: string }>) => {

        const getErrorMessage = (error: AxiosError<{ message?: string; status?: string }>) => {
          if (error.response) {
            return error.response.data?.message ?? error.message;
          }
          if (error.request) {
            return error.message;
          }
          if (error.message === "Network Error") {
            return "Network error detected. Are you offline?";
          }
          return "Something went wrong";
        };

        const errorMessage = getErrorMessage(error);

        if (error.response) {
          switch (error.response.status) {
            case 400:
              this.logger.error("Bad Request");
              break;
            case 401:
              this.logger.error("Unauthorized");
              break;
            case 403:
              this.logger.error("Forbidden");
              break;
            case 404:
              this.logger.error("Not Found");
              break;
            case 500:
              this.logger.error("Internal Server Error");
              break;
            default:
              this.logger.error("Something went wrong");
          }
        } else if (error.request) {
          this.logger.error("No Response");
        } else if (error.message === "Network Error") {
          this.logger.error("Network Error");
        } else {
          this.logger.error("Something went wrong");
        }
        return Promise.reject(new Error(errorMessage));
      }
    );
  }

  public async post<T>(url: string, data: unknown, params?: Record<string, unknown>) {
    return this.httpService.post<unknown, T>(url, data, params);
  }

  public async get<T>(url: string, params?: Record<string, unknown>) {
    return this.httpService.get<T>(url, params);
  }

  public async put<T>(url: string, data: unknown, params?: Record<string, unknown>) {
    return this.httpService.put<unknown, T>(url, data, params);
  }

  public async delete<T>(url: string, params?: Record<string, unknown>) {
    return this.httpService.delete<T>(url, params);
  }
}
